import React from "react";

import BarraDePercurso from "./BarraDePercurso";
import { variaveisDaRegiao } from "./estiloDaRegiao";
import { indiceFaseAtual, totalConcluidas } from "./motorFases";
import Tabuleiro from "./Tabuleiro";

import "../styles/trilhaLudica.css";
import "../styles/aberturaTrilha.css";

/**
 * Trilha lúdica — componente de APRESENTAÇÃO.
 *
 * Não conhece atividade, item, allocation, execution nem qualquer conceito do
 * AVA: recebe um modelo pronto e devolve cliques. É o que permite plugar a
 * trilha em outro host escrevendo só um adaptador (ver ../README.md).
 *
 * Também não guarda progresso. Quem sabe o que está concluído é quem monta o
 * modelo — normalmente a partir do servidor. Re-renderizar com um modelo novo
 * é o mecanismo de avanço.
 *
 * @param {object}   trilha            modelo neutro (contrato em ../README.md)
 * @param {number}   trilha.totalFases
 * @param {boolean}  trilha.sequencial se falso, nenhuma fase bloqueia
 * @param {Array}    trilha.fases      [{ numero, titulo, concluida, referencia }]
 * @param {Function} onAbrirFase       recebe a fase clicada; `fase.referencia`
 *                                     é o que o host precisar (no AVA, o item)
 * @param {string}   [avatar]          URL da figura que acompanha a fase atual
 * @param {string}   [aviso]           texto de alerta acima do tabuleiro; o host
 *                                     decide quando e o que dizer
 * @param {object}   [regiao]          descritor da região: a arte do tabuleiro
 *                                     (`tabuleiro`), a paleta (`cores`) e o `id`,
 *                                     que vira `data-tl-region`. Sem ele a trilha
 *                                     desenha os nós em linha reta e usa o cenário
 *                                     padrão do CSS — funciona, e a falta se vê.
 *                                     Era uma STRING (só o id) até 05/08/2026,
 *                                     quando as 48 amostras da estrada saíram de
 *                                     dentro do `Tabuleiro` e viraram dado da
 *                                     região.
 * @param {string}   [mensagemVazia]   texto quando não há fases
 * @param {boolean}  [adiarPassagem]   o host tem algo por cima (no AVA, a
 *                                     cerimônia do card): a troca de leva fica
 *                                     congelada e a cortina espera. Sem isto a
 *                                     transição aconteceria atrás do que o host
 *                                     está mostrando, e ninguém a veria.
 */
function Trilha({
  trilha,
  onAbrirFase,
  avatar,
  aviso,
  regiao,
  // Os dois sorteios, `{ comuns, raras }` (ver ./recompensaDaFase.js). Só o das
  // RARAS é usado aqui, e só para uma coisa: decidir se o nó concluído de fim de
  // leva acende a estrela de "carta conquistada". Ausente, nenhuma fase rara
  // acende — o mesmo "errar para o lado de não entregar" do resto do módulo.
  sorteio,
  mensagemVazia = "Esta trilha ainda não tem fases para percorrer.",
  adiarPassagem = false
}) {
  if (!trilha || trilha.totalFases === 0) {
    return (
      <div className="trilha-ludica">
        <p className="trilha-ludica__vazio">{mensagemVazia}</p>
      </div>
    );
  }

  const concluidas = totalConcluidas(trilha.fases);
  const indiceAtual = indiceFaseAtual(trilha.fases, trilha.sequencial);
  const terminou = indiceAtual === -1;

  return (
    // As custom properties vão TAMBÉM aqui, e não só na moldura da tela cheia,
    // porque a `Trilha` tem de bastar sozinha: é ela que um host novo monta, e é
    // ela que `nucleo/Trilha.test.jsx` renderiza sem nada do AVA em volta.
    <div
      className="trilha-ludica"
      data-tl-region={regiao && regiao.id}
      style={variaveisDaRegiao(regiao)}
    >
      <div className="trilha-ludica__cabecalho">
        <span className="trilha-ludica__contador">
          {/* A barra vem ANTES do texto no DOM e à esquerda dele na tela: ela é o
              percurso, e o texto é a legenda dela. Invertido, o número viraria o
              assunto e a barra um enfeite. */}
          <BarraDePercurso
            totalFases={trilha.totalFases}
            concluidas={concluidas}
            indiceAtual={indiceAtual}
          />
          {terminou
            ? "Trilha concluída!"
            : `Fase ${indiceAtual + 1} de ${trilha.totalFases}`}
        </span>
      </div>

      {aviso && <p className="trilha-ludica__aviso">{aviso}</p>}

      <Tabuleiro
        trilha={trilha}
        avatar={avatar}
        onAbrirFase={onAbrirFase}
        adiarPassagem={adiarPassagem}
        sorteio={sorteio}
        tabuleiro={regiao && regiao.tabuleiro}
        mascoteCabeca={regiao && regiao.mascote && regiao.mascote.cabeca}
      />

      {/* Classe própria em vez do `sr-only` do Bootstrap: o módulo não pode
          depender do CSS do host para ser portável. */}
      <span className="trilha-ludica__so-leitor" aria-live="polite">
        {concluidas} de {trilha.totalFases} fases concluídas
      </span>
    </div>
  );
}

export default Trilha;
